import React, { useState, useEffect, useMemo, useRef } from "react";
import { COLORS } from "../../../theme/colors";
import { MATERIALS } from "../../../theme/materials";
import Icon from "../../../components/Icon";
import { Button, TogglePill } from "../../../components/ui";

// Mac vs others — show ⌘ on Apple, Ctrl elsewhere. Cheap UA sniff,
// good enough for the kbd hint label (the actual hotkey listener
// accepts both).
const IS_MAC = typeof navigator !== "undefined"
  && /Mac|iPhone|iPad|iPod/.test(navigator.platform || "");
const CMD_LABEL = IS_MAC ? "⌘K" : "Ctrl K";

/**
 * LibrarySearchToolbar — Option C of the Agents-page filter UX (replaces
 * LibrarySearchBar's flat chip-rows layout).
 *
 * Layout:
 *   ┌──────────────────────────────────────────────────────────────────┐
 *   │ [🔍 Search agents…  ✕]   [⚙ Filters · N]   [Sort: Modified ▾]   │
 *   │                                              <toolbarTrailing/> │
 *   ├──────────────────────────────────────────────────────────────────┤
 *   │ Active: ⊘ Live  ⊘ Voice                       Clear all          │
 *   └──────────────────────────────────────────────────────────────────┘
 *
 * The Filters button opens a popover containing status / kind / channel
 * controls. Search and sort stay always-visible because they're the
 * most-frequent operations. Active filters render as removable chips
 * below the toolbar so the current state is always clear at a glance.
 *
 * Used by AgentLibraryView in BOTH grid and list view (replaces the
 * legacy PageHeader.filters={...} slot for that page). agent-experience-
 * redesign-v1.md §4.4 (D7) — followup that consolidates the duplicate
 * search/filter surfaces.
 *
 * Props:
 *   value             — { search, status, kind, channel, sort }
 *   onChange(next)    — fires on every commit (debounced search,
 *                       Apply button in popover, chip dismiss).
 *   agents            — full unfiltered list, used for chip count hints
 *   toolbarTrailing   — optional trailing slot (ViewModeToggle, etc.)
 */

const SORT_OPTIONS = [
  { value: "last_used", label: "Last used"        },
  { value: "modified",  label: "Recently modified" },
  { value: "alpha",     label: "Alphabetical"      },
];

const STATUS_OPTIONS = [
  { value: "all",    label: "All"    },
  { value: "live",   label: "Live"   },
  { value: "paused", label: "Paused" },
  { value: "draft",  label: "Draft"  },
];

const KIND_OPTIONS = [
  { value: "all",        label: "All kinds"  },
  { value: "regular",    label: "Regular"    },
  { value: "supervisor", label: "Supervisor" },
];

const CHANNEL_OPTIONS = [
  { value: "",         label: "Any" },
  { value: "chat",     label: "Chat" },
  { value: "voice",    label: "Voice" },
  { value: "sms",      label: "SMS" },
  { value: "email",    label: "Email" },
  { value: "whatsapp", label: "WhatsApp" },
  { value: "webhook",  label: "Webhook" },
];

export const DEFAULT_SEARCH_BAR = {
  search: "", status: "all", kind: "all", channel: "", category: "", sort: "modified",
};

// Per-axis counts so the Filters popover can show "Live (8)" hints.
// Extracted (named export) so the tally logic can be unit-tested without
// mounting the toolbar. `.all` on status + kind reflects the full length.
export function computeCounts(agents = []) {
  const c = { status: {}, kind: {}, channel: {} };
  for (const a of agents) {
    const s = a.status || "draft";
    c.status[s] = (c.status[s] || 0) + 1;
    const k = a.agent_kind || "regular";
    c.kind[k] = (c.kind[k] || 0) + 1;
    for (const ch of (a.channels || [])) {
      c.channel[ch] = (c.channel[ch] || 0) + 1;
    }
  }
  c.status.all = agents.length;
  c.kind.all = agents.length;
  return c;
}

// Active-filter list drives the "Filters · N" badge AND the Active-chips
// row. Extracted (named export) for unit-testing the axis/label mapping.
export function computeActiveFilters(value) {
  const list = [];
  if (value?.status && value.status !== "all") {
    const opt = STATUS_OPTIONS.find(o => o.value === value.status);
    list.push({ axis: "status", label: opt?.label || value.status, value: value.status });
  }
  if (value?.kind && value.kind !== "all") {
    const opt = KIND_OPTIONS.find(o => o.value === value.kind);
    list.push({ axis: "kind", label: opt?.label || value.kind, value: value.kind });
  }
  if (value?.channel) {
    const opt = CHANNEL_OPTIONS.find(o => o.value === value.channel);
    list.push({ axis: "channel", label: opt?.label || value.channel, value: value.channel });
  }
  return list;
}


export default function LibrarySearchToolbar({
  value, onChange, agents = [], toolbarTrailing, leadingSlot,
}) {

  // Per-axis counts so the Filters popover can show "Live (8)" hints.
  const counts = useMemo(() => computeCounts(agents), [agents]);

  // Active-filter count drives the "Filters · N" badge AND the
  // Active-chips row below the toolbar.
  const activeFilters = useMemo(() => computeActiveFilters(value), [value]);

  const [popoverOpen, setPopoverOpen] = useState(false);
  const filterButtonRef = useRef(null);
  const [searchInput, setSearchInput] = useState(value?.search || "");

  // Sync external clear (e.g. "Clear all" from parent)
  useEffect(() => {
    if (!value?.search && searchInput) setSearchInput("");
  }, [value?.search]);

  // Debounce search → onChange
  useEffect(() => {
    const t = setTimeout(() => {
      if ((value?.search || "") !== searchInput) {
        onChange({ ...value, search: searchInput });
      }
    }, 200);
    return () => clearTimeout(t);
  }, [searchInput]);

  function dropFilter(axis) {
    const next = { ...value };
    if (axis === "status") next.status = "all";
    else if (axis === "kind") next.kind = "all";
    else if (axis === "channel") next.channel = "";
    onChange(next);
  }

  function clearAll() {
    setSearchInput("");
    onChange({ ...DEFAULT_SEARCH_BAR, sort: value?.sort || "modified" });
  }

  // Glass pill base style — shared across search/filters/sort buttons.
  const pillBase = {
    display: "flex", alignItems: "center", gap: 8,
    padding: "9px 14px", fontSize: 12, fontWeight: 500,
    color: COLORS.text,
    ...MATERIALS.thinGlass(),
    borderRadius: 12,
    cursor: "pointer",
    whiteSpace: "nowrap",
    transition: "transform 0.12s, box-shadow 0.12s, border-color 0.12s",
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {/* Top row: leading slot (stats etc.) on left, controls flush right */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        {/* Leading slot consumes the left side; controls auto-margin to the right. */}
        {leadingSlot && (
          <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
            {leadingSlot}
          </div>
        )}

        <div style={{
          display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
          marginLeft: "auto",
        }}>
          {/* Search input — filters list directly as user types */}
          <div style={{
            ...pillBase,
            padding: 0,
            minWidth: 220, maxWidth: 300,
            overflow: "hidden",
          }}>
            <span style={{ paddingLeft: 12, display: "flex", alignItems: "center", flexShrink: 0 }}>
              <Icon name="action.search" size={14} color={COLORS.textDim} />
            </span>
            <input
              type="text"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              placeholder="Search agents…"
              style={{
                flex: 1,
                background: "transparent",
                border: "none",
                outline: "none",
                color: COLORS.text,
                fontSize: 12,
                fontWeight: 500,
                padding: "9px 6px",
                minWidth: 0,
              }}
            />
            {searchInput && (
              <button
                type="button"
                onClick={() => setSearchInput("")}
                style={{
                  padding: "0 10px",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  color: COLORS.textDim,
                  fontSize: 14,
                  lineHeight: 1,
                  display: "flex",
                  alignItems: "center",
                }}
                title="Clear search"
              >×</button>
            )}
          </div>

          {/* Filters button */}
          <div style={{ position: "relative" }}>
            <button
              ref={filterButtonRef}
              type="button"
              onClick={() => setPopoverOpen(o => !o)}
              style={{
                ...pillBase,
                fontWeight: 600,
                color: activeFilters.length ? COLORS.accent : COLORS.text,
                ...(activeFilters.length ? { borderColor: COLORS.accent } : {}),
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-1px)";
                e.currentTarget.style.boxShadow = `0 6px 20px ${COLORS.accent}26`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
              }}
              aria-expanded={popoverOpen}
              aria-haspopup="dialog"
            >
              <Icon name="action.filter" size={13} color={activeFilters.length ? COLORS.accent : COLORS.textMuted} />
              Filters{activeFilters.length > 0 ? ` · ${activeFilters.length}` : ""}
            </button>
            {popoverOpen && (
              <FilterPopover
                value={value}
                counts={counts}
                onClose={() => setPopoverOpen(false)}
                onApply={(next) => { onChange(next); setPopoverOpen(false); }}
                anchorRef={filterButtonRef}
              />
            )}
          </div>

          {/* Sort — chevron rendered as an absolutely-positioned <span>
              child of a wrapper <div>. Inline-style background-image on
              the <select> was unreliable: the `pillBase` spread injects
              MATERIALS.thinGlass() which contains `background: rgba(...)`
              (shorthand), and Safari/some Chrome builds apply the
              shorthand authoritatively, overriding subsequent
              `backgroundRepeat: no-repeat` longhand → chevron tiles
              across the entire pill. Decoupling the chevron from the
              <select>'s background sidesteps the cascade conflict. */}
          <div style={{ position: "relative", display: "inline-block" }}>
            <select
              value={value?.sort || "modified"}
              onChange={(e) => onChange({ ...value, sort: e.target.value })}
              style={{
                ...pillBase,
                fontWeight: 600,
                outline: "none",
                appearance: "none",
                WebkitAppearance: "none",
                MozAppearance: "none",
                paddingRight: 28,
              }}
              title="Sort agents"
            >
              {SORT_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>Sort: {o.label}</option>
              ))}
            </select>
            <span
              aria-hidden="true"
              style={{
                position: "absolute",
                right: 10,
                top: "50%",
                transform: "translateY(-50%)",
                width: 10,
                height: 10,
                pointerEvents: "none",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
                   stroke={COLORS.textDim} strokeWidth="2.5"
                   strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </span>
          </div>

          {/* Trailing slot for view-mode + density toggles */}
          {toolbarTrailing && (
            <div style={{ display: "flex", gap: 6 }}>
              {toolbarTrailing}
            </div>
          )}
        </div>
      </div>

      {/* Active-chips row — only renders when at least one filter active */}
      {activeFilters.length > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{
            fontSize: 10, fontWeight: 700, color: COLORS.textDim,
            textTransform: "uppercase", letterSpacing: "0.06em",
          }}>Active:</span>
          {activeFilters.map(af => (
            <TogglePill
              key={`${af.axis}-${af.value}`}
              active
              size="sm"
              onClick={() => dropFilter(af.axis)}
              title={`Remove ${af.axis} filter`}
              style={{ textTransform: "capitalize" }}
            >
              {af.label}
              <Icon name="action.close" size={10} color={COLORS.accent} />
            </TogglePill>
          ))}
          <button
            type="button"
            onClick={clearAll}
            style={{
              padding: "3px 8px", fontSize: 11, fontWeight: 600,
              color: COLORS.textMuted, background: "transparent",
              border: "none", cursor: "pointer",
              textDecoration: "underline",
            }}
          >
            Clear all
          </button>
        </div>
      )}
    </div>
  );
}


// ─── Popover ──────────────────────────────────────────────────────


function FilterPopover({ value, counts, onClose, onApply, anchorRef }) {
  // Pending state — user stages changes inside the popover, then
  // Apply commits them. Cancel discards. Esc / outside-click is
  // also Cancel.
  const [pending, setPending] = useState({
    status: value?.status || "all",
    kind: value?.kind || "all",
    channel: value?.channel || "",
  });
  const popoverRef = useRef(null);

  // Outside-click + Esc dismiss
  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose();
    }
    function onMouse(e) {
      if (popoverRef.current && popoverRef.current.contains(e.target)) return;
      if (anchorRef?.current && anchorRef.current.contains(e.target)) return;
      onClose();
    }
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onMouse);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onMouse);
    };
  }, [onClose, anchorRef]);

  function reset() {
    setPending({ status: "all", kind: "all", channel: "" });
  }

  return (
    <div
      ref={popoverRef}
      role="dialog"
      aria-label="Filter agents"
      style={{
        position: "absolute",
        top: "calc(100% + 6px)",
        left: 0,
        zIndex: 50,
        minWidth: 320,
        maxWidth: 400,
        background: COLORS.surface,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 10,
        boxShadow: "0 12px 32px rgba(0,0,0,0.18)",
        padding: 14,
        display: "flex", flexDirection: "column", gap: 14,
      }}
    >
      <PopoverGroup label="Status" options={STATUS_OPTIONS}
        selected={pending.status} counts={counts.status}
        onSelect={(v) => setPending(p => ({ ...p, status: v }))} />

      <PopoverGroup label="Kind" options={KIND_OPTIONS}
        selected={pending.kind} counts={counts.kind}
        onSelect={(v) => setPending(p => ({ ...p, kind: v }))} />

      <PopoverGroup label="Channel" options={CHANNEL_OPTIONS}
        selected={pending.channel} counts={counts.channel}
        onSelect={(v) => setPending(p => ({ ...p, channel: v }))} />

      {/* Footer */}
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        gap: 10, paddingTop: 10, borderTop: `1px solid ${COLORS.border}`,
      }}>
        <Button variant="secondary" size="sm" onClick={reset}>Reset</Button>
        <div style={{ display: "flex", gap: 6 }}>
          <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
          <Button variant="primary" size="sm"
            onClick={() => onApply({ ...value, ...pending })}>Apply</Button>
        </div>
      </div>
    </div>
  );
}


function PopoverGroup({ label, options, selected, onSelect, counts = {} }) {
  return (
    <div>
      <div style={{
        fontSize: 10, fontWeight: 700, color: COLORS.textDim,
        textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6,
      }}>{label}</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {options.map(o => {
          const active = selected === o.value;
          const n = counts[o.value];
          const showCount = n != null && o.value && o.value !== "all";
          return (
            <TogglePill
              key={o.value || "any"}
              active={active}
              size="sm"
              onClick={() => onSelect(o.value)}
              count={showCount ? n : undefined}
            >
              {o.label}
            </TogglePill>
          );
        })}
      </div>
    </div>
  );
}


// applySearchFilter used to live here as the client-side filter+sort
// applied to the full agent list. Removed 2026-08-02: AgentLibraryView
// now sends search/status/kind/channel/category/sort to the server via
// agentApi.listPaged() (GET /api/agents), which applies the identical
// semantics (same expert-hide, same fields, same sort orders) — see
// backend/app/routers/agents.py::list_agents. Client-side filtering
// only ever operated on the page already in hand, so it silently broke
// the moment pagination made "the page in hand" != "every agent".
//
// `counts` above still needs the FULL (expert-hidden) agent list for
// its "Live (8)" hints — that comes from AgentLibraryView's separate,
// unpaginated agentApi.list() call, unaffected by this removal.
